import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Box, Stack, Typography, Paper, Button, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, IconButton,
  Grid, Checkbox, Card, CardContent, Alert, CircularProgress,
  LinearProgress, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import {
  SwapHoriz as ReassignIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  AccountTree as WorkflowsIcon,
} from '@mui/icons-material'
import apiService from '../services/api.service'
import { useDispatch } from 'react-redux'
import { showSnackbar } from '../store/slices/uiSlice'

type Workflow = {
  id?: number
  workflowid?: number
  flowname: string
  requisitiontype: string
  allocationtype: string
  allocationcode?: string
  stages: Array<{
    name: string | null
    userIds: number[]
    rule: 'ANY' | 'ALL'
  }>
  autoposttosap?: boolean
  facilitytype?: string | null
  cycle?: string | null
}

type User = {
  id: number
  email: string
  fullname: string
  department?: string
  role?: string
  sapusercode?: string
}

type BulkOperation = {
  type: 'assign' | 'remove' | 'reassign' | 'modify'
  targetWorkflows: number[]
  targetUsers: number[]
  details?: any
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress?: number
  result?: any
  error?: string
}

export default function BulkWorkflowManagement() {
  const dispatch = useDispatch()
  const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined

  // Data states
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [filteredWorkflows, setFilteredWorkflows] = useState<Workflow[]>([])
  const [selectedWorkflows, setSelectedWorkflows] = useState<number[]>([])
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])

  // Filters
  const [filters, setFilters] = useState<{
    requisitiontype?: string
    allocationtype?: string
    allocationcode?: string
    facilitytype?: string
    cycle?: string
    searchWorkflow?: string
    searchUser?: string
  }>({})

  // Operation states
  const [operationType, setOperationType] = useState<'assign' | 'remove' | 'reassign' | 'modify' | null>(null)
  const [operationDialogOpen, setOperationDialogOpen] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [pendingOperation, setPendingOperation] = useState<BulkOperation | null>(null)
  const [operationHistory, setOperationHistory] = useState<BulkOperation[]>([])
  const [processing, setProcessing] = useState(false)

  // Modify operation specific
  const [targetStage, setTargetStage] = useState<number>(1)
  const [modifyAction, setModifyAction] = useState<'add' | 'remove' | 'replace' | 'changeRule'>('add')
  const [newRule, setNewRule] = useState<'ANY' | 'ALL'>('ANY')
  const [reassignToUsers, setReassignToUsers] = useState<number[]>([])

  // Stats
  const [stats, setStats] = useState<any>(null)

  const loadStats = useCallback(async () => {
    try {
      const res = await apiService.getWorkflowStats()
      setStats(res?.data || {})
    } catch (error: any) {
      console.error('Failed to load stats:', error)
    }
  }, [])

  const loadWorkflows = useCallback(async () => {
    try {
      const res = await apiService.listApprovalWorkflows({ companyId: selectedCompanyId })
      setWorkflows(res?.data || [])
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to load workflows', severity: 'error' }))
    }
  }, [selectedCompanyId, dispatch])

  const loadUsers = useCallback(async () => {
    try {
      const res = await apiService.getUsers()
      setUsers(res?.data || [])
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to load users', severity: 'error' }))
    }
  }, [dispatch])

  // Fetch data
  useEffect(() => {
    loadWorkflows()
    loadUsers()
    loadStats()
  }, [loadWorkflows, loadUsers, loadStats])

  // Filter workflows
  useEffect(() => {
    let filtered = workflows

    if (filters.requisitiontype) {
      filtered = filtered.filter(w => w.requisitiontype === filters.requisitiontype)
    }
    if (filters.allocationtype) {
      filtered = filtered.filter(w => w.allocationtype === filters.allocationtype)
    }
    if (filters.allocationcode) {
      filtered = filtered.filter(w => w.allocationcode === filters.allocationcode)
    }
    if (filters.facilitytype) {
      filtered = filtered.filter(w => w.facilitytype === filters.facilitytype)
    }
    if (filters.cycle) {
      filtered = filtered.filter(w => w.cycle === filters.cycle)
    }
    if (filters.searchWorkflow) {
      const search = filters.searchWorkflow.toLowerCase()
      filtered = filtered.filter(w =>
        w.flowname?.toLowerCase().includes(search) ||
        w.allocationcode?.toLowerCase().includes(search)
      )
    }

    setFilteredWorkflows(filtered)
  }, [workflows, filters])

  const filteredUsers = useMemo(() => {
    if (!filters.searchUser) return users
    const search = filters.searchUser.toLowerCase()
    return users.filter(u =>
      u.fullname?.toLowerCase().includes(search) ||
      u.email?.toLowerCase().includes(search) ||
      u.department?.toLowerCase().includes(search)
    )
  }, [users, filters.searchUser])

  // Bulk operations
  const executeBulkOperation = async (operation: BulkOperation) => {
    setProcessing(true)
    const updatedOp = { ...operation, status: 'processing' as const, progress: 0 }
    setOperationHistory(prev => [updatedOp, ...prev])

    try {
      let result
      switch (operation.type) {
        case 'assign':
          result = await apiService.bulkAssignUsersToWorkflows({
            workflowIds: operation.targetWorkflows,
            userIds: operation.targetUsers,
            role: operation.details.role,
            stageNumber: operation.details.stageNumber
          })
          break

        case 'remove':
          result = await apiService.bulkRemoveUsersFromWorkflows({
            workflowIds: operation.targetWorkflows,
            userIds: operation.targetUsers,
            role: operation.details.role
          })
          break

        case 'reassign':
          result = await apiService.bulkReassignUsers({
            workflowIds: operation.targetWorkflows,
            fromUserIds: operation.targetUsers,
            toUserIds: operation.details.toUserIds,
            preserveRoles: operation.details.preserveRoles ?? true
          })
          break

        case 'modify':
          result = await apiService.bulkModifyWorkflowStage({
            workflowIds: operation.targetWorkflows,
            stageNumber: operation.details.stageNumber,
            updates: operation.details.updates
          })
          break

        default:
          throw new Error('Unknown operation type')
      }

      const completedOp = { ...updatedOp, status: 'completed' as const, result }
      setOperationHistory(prev => [completedOp, ...prev.slice(1)])
      dispatch(showSnackbar({
        message: `Operation completed: ${result?.data?.message || 'Success'}`,
        severity: 'success'
      }))
      await loadWorkflows()
      await loadStats()

    } catch (error: any) {
      const errorOp = { ...updatedOp, status: 'error' as const, error: error.message }
      setOperationHistory(prev => [errorOp, ...prev.slice(1)])
      dispatch(showSnackbar({
        message: error.message || 'Operation failed',
        severity: 'error'
      }))
    } finally {
      setProcessing(false)
      setConfirmDialogOpen(false)
      setOperationDialogOpen(false)
    }
  }

  const prepareOperation = () => {
    if (!operationType || selectedWorkflows.length === 0) {
      dispatch(showSnackbar({ message: 'Please select operation type and workflows', severity: 'warning' }))
      return
    }

    let details: any = {}

    switch (operationType) {
      case 'assign':
        if (selectedUsers.length === 0) {
          dispatch(showSnackbar({ message: 'Please select users to assign', severity: 'warning' }))
          return
        }
        details = {
          role: 'approver',
          stageNumber: targetStage
        }
        break

      case 'remove':
        if (selectedUsers.length === 0) {
          dispatch(showSnackbar({ message: 'Please select users to remove', severity: 'warning' }))
          return
        }
        details = { role: 'approver' }
        break

      case 'reassign':
        if (selectedUsers.length === 0 || reassignToUsers.length === 0) {
          dispatch(showSnackbar({ message: 'Please select users to reassign from and to', severity: 'warning' }))
          return
        }
        details = {
          toUserIds: reassignToUsers,
          preserveRoles: true
        }
        break

      case 'modify':
        details = {
          stageNumber: targetStage,
          updates: {}
        }
        if (modifyAction === 'add') {
          details.updates.addUsers = selectedUsers
        } else if (modifyAction === 'remove') {
          details.updates.removeUsers = selectedUsers
        } else if (modifyAction === 'replace') {
          details.updates.replaceUsers = selectedUsers
        } else if (modifyAction === 'changeRule') {
          details.updates.rule = newRule
        }
        break
    }

    const operation: BulkOperation = {
      type: operationType,
      targetWorkflows: selectedWorkflows,
      targetUsers: selectedUsers,
      details,
      status: 'pending'
    }

    setPendingOperation(operation)
    setConfirmDialogOpen(true)
  }

  // Workflow columns
  const workflowColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'flowname', headerName: 'Flow Name', width: 200 },
    { field: 'requisitiontype', headerName: 'Req Type', width: 120 },
    { field: 'allocationtype', headerName: 'Allocation', width: 120 },
    { field: 'allocationcode', headerName: 'Code', width: 150 },
    {
      field: 'stages',
      headerName: 'Stages',
      width: 150,
      renderCell: (params) => {
        const stages = params.row.stages || []
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {stages.map((stage: any, idx: number) => (
              <Chip
                key={idx}
                size="small"
                label={`${idx + 1}: ${stage.name || '-'} (${stage.userIds?.length || 0} users, ${stage.rule})`}
                variant="outlined"
              />
            ))}
          </Box>
        )
      }
    },
    {
      field: 'actions',
      headerName: 'Users',
      width: 200,
      renderCell: (params) => {
        const stages = params.row.stages || []
        const totalUsers = stages.reduce((sum: number, s: any) => sum + (s.userIds?.length || 0), 0)
        return <Typography variant="body2">{totalUsers} users across {stages.length} stages</Typography>
      }
    },
  ]

  // User columns
  const userColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'fullname', headerName: 'Name', width: 200 },
    { field: 'email', headerName: 'Email', width: 250 },
    { field: 'department', headerName: 'Department', width: 180 },
    { field: 'role', headerName: 'Role', width: 150 },
  ]

  const getWorkflowRowId = (row: Workflow) => Number(row.id ?? row.workflowid)
  const getUserRowId = (row: any) => Number(row.id ?? row.userid ?? row.UserID)

  return (
    <Box
      component="section"
      aria-labelledby="bwm-heading"
      sx={{
        width: '100%',
        minWidth: 0,
        p: { xs: 1, sm: 1.5 },
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Paper
        sx={{
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderLeft: '4px solid #0A6ED1',
          boxShadow: 2,
          overflow: 'hidden',
        }}
      >
      {/* Header */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, bgcolor: '#f5f5f5', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography
          component="h1"
          id="bwm-heading"
          variant="h5"
          sx={{ fontWeight: 600, mb: 1, fontSize: { xs: 18, sm: 22, md: 24 } }}
        >
          Bulk Workflow Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Intelligent bulk operations for managing 4500+ workflows across 300+ projects and 100+ users
        </Typography>

        {/* Stats */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <WorkflowsIcon color="primary" />
                    <Box>
                      <Typography variant="h6">{stats.totalWorkflows || workflows.length}</Typography>
                      <Typography variant="caption" color="text.secondary">Total Workflows</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AssignmentIcon color="success" />
                    <Box>
                      <Typography variant="h6">{stats.totalProjects || new Set(workflows.map(w => w.allocationcode)).size}</Typography>
                      <Typography variant="caption" color="text.secondary">Projects</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <GroupIcon color="info" />
                    <Box>
                      <Typography variant="h6">{users.length}</Typography>
                      <Typography variant="caption" color="text.secondary">Users</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonAddIcon color="warning" />
                    <Box>
                      <Typography variant="h6">{stats.totalAssignments || 'N/A'}</Typography>
                      <Typography variant="caption" color="text.secondary">Total Assignments</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Quick Actions */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mb: 2,
            flexWrap: 'wrap',
            overflowX: 'auto',
            pb: 0.25,
            '&::-webkit-scrollbar': { height: 6 },
          }}
        >
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => { setOperationType('assign'); setOperationDialogOpen(true); }}
            disabled={selectedWorkflows.length === 0 || processing}
          >
            Assign Users
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<PersonRemoveIcon />}
            onClick={() => { setOperationType('remove'); setOperationDialogOpen(true); }}
            disabled={selectedWorkflows.length === 0 || processing}
          >
            Remove Users
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<ReassignIcon />}
            onClick={() => { setOperationType('reassign'); setOperationDialogOpen(true); }}
            disabled={selectedWorkflows.length === 0 || processing}
          >
            Reassign Users
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<EditIcon />}
            onClick={() => { setOperationType('modify'); setOperationDialogOpen(true); }}
            disabled={selectedWorkflows.length === 0 || processing}
          >
            Modify Stages
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={loadWorkflows}
            disabled={processing}
          >
            Refresh
          </Button>
        </Stack>

        {/* Selection Summary */}
        {selectedWorkflows.length > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <strong>{selectedWorkflows.length}</strong> workflows selected |
            {selectedUsers.length > 0 && <strong> {selectedUsers.length} users selected</strong>}
          </Alert>
        )}
      </Paper>

      <Grid container spacing={2} sx={{ minWidth: 0 }}>
        {/* Workflows Panel */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ height: 600, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', minWidth: 0 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Workflows ({filteredWorkflows.length})</Typography>

              {/* Filters */}
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Req Type</InputLabel>
                    <Select
                      value={filters.requisitiontype || ''}
                      label="Req Type"
                      onChange={(e) => setFilters(f => ({ ...f, requisitiontype: e.target.value || undefined }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Inventory">Inventory</MenuItem>
                      <MenuItem value="Service">Service</MenuItem>
                      <MenuItem value="FixedAsset">Fixed Asset</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Allocation</InputLabel>
                    <Select
                      value={filters.allocationtype || ''}
                      label="Allocation"
                      onChange={(e) => setFilters(f => ({ ...f, allocationtype: e.target.value || undefined }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Project">Project</MenuItem>
                      <MenuItem value="CostCenter">Cost Center</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search Workflow"
                    value={filters.searchWorkflow || ''}
                    onChange={(e) => setFilters(f => ({ ...f, searchWorkflow: e.target.value }))}
                    InputProps={{
                      endAdornment: filters.searchWorkflow && (
                        <IconButton size="small" onClick={() => setFilters(f => ({ ...f, searchWorkflow: undefined }))}>
                          <ClearIcon />
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <Button
                    size="small"
                    fullWidth
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={() => setFilters({})}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
              <DataGrid
                rows={filteredWorkflows}
                columns={workflowColumns}
                getRowId={getWorkflowRowId}
                checkboxSelection
                disableRowSelectionOnClick
                onRowSelectionModelChange={(newModel) => {
                  const selectedIds = newModel as number[]
                  setSelectedWorkflows(selectedIds)
                }}
                rowSelectionModel={selectedWorkflows}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={{ border: 0, height: 500, minWidth: 1080 }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Users Panel */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ height: 600, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', minWidth: 0 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Users ({filteredUsers.length})</Typography>

              <TextField
                size="small"
                fullWidth
                label="Search Users"
                value={filters.searchUser || ''}
                onChange={(e) => setFilters(f => ({ ...f, searchUser: e.target.value }))}
                InputProps={{
                  endAdornment: filters.searchUser && (
                    <IconButton size="small" onClick={() => setFilters(f => ({ ...f, searchUser: undefined }))}>
                      <ClearIcon />
                    </IconButton>
                  )
                }}
                sx={{ mb: 1 }}
              />

              <Button
                size="small"
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={() => { setSelectedUsers([]); }}
                disabled={selectedUsers.length === 0}
                fullWidth
              >
                Clear Selection ({selectedUsers.length})
              </Button>
            </Box>

            <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
              <DataGrid
                rows={filteredUsers}
                columns={userColumns}
                getRowId={getUserRowId}
                checkboxSelection
                disableRowSelectionOnClick
                onRowSelectionModelChange={(newModel) => {
                  const selectedIds = newModel as number[]
                  setSelectedUsers(selectedIds)
                }}
                rowSelectionModel={selectedUsers}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={{ border: 0, height: 490, minWidth: 760 }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Operation History */}
      {operationHistory.length > 0 && (
        <Paper sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Operation History ({operationHistory.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {operationHistory.slice(0, 10).map((op, idx) => (
                  <Card key={idx} variant="outlined">
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {op.status === 'completed' && <CheckCircleIcon color="success" fontSize="small" />}
                        {op.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                        {op.status === 'processing' && <CircularProgress size={16} />}
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          <strong>{op.type.toUpperCase()}</strong>: {op.targetWorkflows.length} workflows,
                          {op.targetUsers.length} users
                        </Typography>
                        {op.status === 'error' && (
                          <Typography variant="caption" color="error">{op.error}</Typography>
                        )}
                        {op.status === 'completed' && op.result?.data && (
                          <Typography variant="caption" color="success">
                            {op.result.data.message || 'Success'}
                          </Typography>
                        )}
                      </Stack>
                      {op.status === 'processing' && (
                        <LinearProgress variant="indeterminate" sx={{ mt: 1 }} />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}
      </Paper>

      {/* Operation Dialog */}
      <Dialog open={operationDialogOpen} onClose={() => setOperationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {operationType === 'assign' && 'Assign Users to Workflows'}
          {operationType === 'remove' && 'Remove Users from Workflows'}
          {operationType === 'reassign' && 'Reassign Users in Workflows'}
          {operationType === 'modify' && 'Modify Workflow Stages'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedWorkflows.length} workflows selected
          </Alert>

          {operationType === 'assign' && (
            <Stack spacing={2}>
              <Typography variant="body2">
                Assign <strong>{selectedUsers.length} users</strong> to <strong>{selectedWorkflows.length} workflows</strong>
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Target Stage</InputLabel>
                <Select
                  value={targetStage}
                  label="Target Stage"
                  onChange={(e) => setTargetStage(Number(e.target.value))}
                >
                  <MenuItem value={1}>Stage 1</MenuItem>
                  <MenuItem value={2}>Stage 2</MenuItem>
                  <MenuItem value={3}>Stage 3</MenuItem>
                  <MenuItem value={4}>Stage 4</MenuItem>
                  <MenuItem value={5}>Stage 5</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          {operationType === 'remove' && (
            <Typography variant="body2">
              Remove <strong>{selectedUsers.length} users</strong> from <strong>{selectedWorkflows.length} workflows</strong>
            </Typography>
          )}

          {operationType === 'reassign' && (
            <Stack spacing={2}>
              <Typography variant="body2">
                Replace <strong>{selectedUsers.length} users</strong> with new users in <strong>{selectedWorkflows.length} workflows</strong>
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select Replacement Users</InputLabel>
                <Select
                  multiple
                  value={reassignToUsers}
                  label="Select Replacement Users"
                  onChange={(e) => setReassignToUsers(e.target.value as number[])}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((value) => {
                        const user = users.find(u => u.id === value)
                        return <Chip key={value} size="small" label={user?.fullname || value} />
                      })}
                    </Box>
                  )}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      <Checkbox checked={reassignToUsers.indexOf(user.id) > -1} />
                      {user.fullname} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}

          {operationType === 'modify' && (
            <Stack spacing={2}>
              <Typography variant="body2">
                Modify stages in <strong>{selectedWorkflows.length} workflows</strong>
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={modifyAction}
                  label="Action"
                  onChange={(e) => setModifyAction(e.target.value as any)}
                >
                  <MenuItem value="add">Add Users</MenuItem>
                  <MenuItem value="remove">Remove Users</MenuItem>
                  <MenuItem value="replace">Replace Users</MenuItem>
                  <MenuItem value="changeRule">Change Approval Rule</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Target Stage</InputLabel>
                <Select
                  value={targetStage}
                  label="Target Stage"
                  onChange={(e) => setTargetStage(Number(e.target.value))}
                >
                  <MenuItem value={1}>Stage 1</MenuItem>
                  <MenuItem value={2}>Stage 2</MenuItem>
                  <MenuItem value={3}>Stage 3</MenuItem>
                  <MenuItem value={4}>Stage 4</MenuItem>
                  <MenuItem value={5}>Stage 5</MenuItem>
                </Select>
              </FormControl>

              {modifyAction === 'changeRule' && (
                <FormControl fullWidth>
                  <InputLabel>New Rule</InputLabel>
                  <Select
                    value={newRule}
                    label="New Rule"
                    onChange={(e) => setNewRule(e.target.value as any)}
                  >
                    <MenuItem value="ANY">ANY (Any one approval needed)</MenuItem>
                    <MenuItem value="ALL">ALL (All approvals needed)</MenuItem>
                  </Select>
                </FormControl>
              )}

              {modifyAction !== 'changeRule' && (
                <Alert severity={modifyAction === 'add' ? 'success' : modifyAction === 'remove' ? 'error' : 'warning'}>
                  {modifyAction === 'add' && `Will add ${selectedUsers.length} users to stage ${targetStage}`}
                  {modifyAction === 'remove' && `Will remove ${selectedUsers.length} users from stage ${targetStage}`}
                  {modifyAction === 'replace' && `Will replace all users in stage ${targetStage} with ${selectedUsers.length} users`}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOperationDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button
            variant="contained"
            onClick={prepareOperation}
            disabled={processing || (operationType === 'reassign' && reassignToUsers.length === 0)}
          >
            Preview & Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Bulk Operation</DialogTitle>
        <DialogContent>
          {pendingOperation && (
            <Stack spacing={2}>
              <Alert severity="warning">
                This operation will affect <strong>{pendingOperation.targetWorkflows.length} workflows</strong>.
                This action cannot be undone. Are you sure you want to proceed?
              </Alert>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Operation Details:</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2"><strong>Type:</strong> {pendingOperation.type.toUpperCase()}</Typography>
                  <Typography variant="body2"><strong>Workflows:</strong> {pendingOperation.targetWorkflows.length}</Typography>
                  <Typography variant="body2"><strong>Users:</strong> {pendingOperation.targetUsers.length}</Typography>
                  {pendingOperation.details?.stageNumber && (
                    <Typography variant="body2"><strong>Stage:</strong> {pendingOperation.details.stageNumber}</Typography>
                  )}
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => pendingOperation && executeBulkOperation(pendingOperation)}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : undefined}
          >
            {processing ? 'Processing...' : 'Confirm & Execute'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
